import {html, nothing, type TemplateResult} from 'lit';
import {
  characters,
  generateAyogavaha,
  generateConjuncts,
  generateDependentVowels,
  generateVirama,
} from '../characters';

const PANEL_ID = 'dq-instructions-panel';

/**
 * 文字種ごとの件数は数え上げを書かずにデータから出す。
 * 旧実装は 7 通りの組み合わせを手書きしており、実装とずれていた
 * （「母音と依存母音記号：最大26文字」と書かれていたが実際は 443 だった）。
 */
const counts = {
  vowels: characters.vowels.length,
  consonants: characters.consonants.length,
  dependent: generateDependentVowels().length,
  virama: generateVirama().length,
  ayogavaha: generateAyogavaha().length,
  conjuncts: generateConjuncts().length,
  numerals: characters.numerals.length,
};

// 1 行に書く。改行を入れると前後に空白が入り、閉じ括弧が離れる
const link = (href: string, text: string) =>
  html`<a href=${href} target="_blank" rel="noopener" class="text-indigo-700 hover:text-indigo-900 underline">${text}<span class="sr-only">（新しいタブで開きます）</span></a>`;

/**
 * 使用方法。折りたたみ 1 段の中を「使い方」（短い）と「文字種の解説」（長い）に分ける。
 *
 * 解説は 375px で約 1,100px の長文になり、使い方の 3 行を探せなかった。
 * 別の折りたたみ帯にすると初期描画の高さが変わる（README のプレースホルダに響く）ので、
 * 帯は 1 つのまま、長いほうを中の <details> に入れる。
 */
export function instructions(expanded: boolean, onToggle: () => void): TemplateResult {
  return html`
    <div class="bg-indigo-50 rounded-xl p-5 mt-8">
      <h2 class="mb-0">
        <button
          type="button"
          aria-expanded=${expanded ? 'true' : 'false'}
          aria-controls=${PANEL_ID}
          @click=${onToggle}
          class="flex w-full justify-between items-center py-5 -my-5 text-left cursor-pointer rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-700 focus-visible:ring-offset-2"
        >
          <span class="text-xl font-semibold text-indigo-800">使用方法 (Instructions)</span>
          <span aria-hidden="true" class="text-indigo-600">
            ${expanded
              ? html`<svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <path d="m18 15-6-6-6 6" />
                </svg>`
              : html`<svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <path d="m6 9 6 6 6-6" />
                </svg>`}
          </span>
        </button>
      </h2>

      ${expanded
        ? html`<div id=${PANEL_ID} class="mt-4 text-gray-700 space-y-4 leading-relaxed">
            <p>
              画面に出た字の読み方を、下の選択肢から選びます。正解すると自動的に次へ進みます。
              間違えたときは、基になる字との対比を確認してから「次へ」を押してください。
            </p>

            <ul class="list-disc pl-5 space-y-2">
              <li>
                <strong class="text-indigo-700">クイズ設定</strong>で、練習する文字の種類と、
                一度に出題する文字数を選びます。次に開いたときも同じ設定で始まります。
              </li>
              <li>
                はじめて出る字には、最初のあいだ読み方を添えて出します。覚えてきた字は、読み方から字を選ぶ向きでも出ます（学習の記録で切れます）。
              </li>
              <li>
                <strong class="text-indigo-700">学習の記録</strong>は、このブラウザにだけ保存されます
                （どこにも送信しません）。間違えた字や時間のかかった字が多めに出題され、
                覚えた字も忘れないように時々出ます。出題ボックスの「記録」で進み具合を確認できます。
              </li>
              <li>
                <strong class="text-indigo-700">別の文字にする</strong
                >ボタンを押すと、設定した文字数の中から新しい文字セットが選ばれます。
                学習の記録は消えません。
              </li>
            </ul>

            <!-- 開閉の見た目は外側の帯と同じシェブロンに揃える（既定の三角は :host の
                 list-style: none に消されうるし、1 つのパネルに 2 種類の開閉表現を置かない） -->
            <details class="group rounded-lg bg-white/60 px-4 py-3">
              <summary
                class="flex justify-between items-center gap-2 cursor-pointer list-none [&::-webkit-details-marker]:hidden font-medium text-indigo-800 rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-700"
              >
                文字の種類ごとの説明
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  aria-hidden="true"
                  class="shrink-0 text-indigo-600 transition-transform group-open:rotate-180"
                >
                  <path d="m6 9 6 6 6-6" />
                </svg>
              </summary>
              <div class="mt-3 space-y-4">
                <ul class="list-disc pl-5 space-y-1 text-sm text-gray-600">
                  <li>母音：${counts.vowels}文字（参考：${link('https://blog.sitarama.jp/?p=2222', 'サンスクリット語の母音について')}）</li>
                  <li>子音：${counts.consonants}文字（参考：${link('https://blog.sitarama.jp/?p=2257', 'サンスクリット語の子音について')}）</li>
                  <li>
                    依存母音記号：${counts.dependent}文字（子音${counts.consonants} ×
                    母音記号10 の組み合わせ）
                  </li>
                  <li>ヴィラーマ：${counts.virama}文字</li>
                  <li>
                    アヌスヴァーラ・ヴィサルガ：${counts.ayogavaha}文字（子音${counts.consonants}
                    × 2 記号）
                  </li>
                  <li>結合子音：${counts.conjuncts}文字</li>
                  <li>数字：${counts.numerals}文字</li>
                </ul>

                <p>
                  <strong class="text-indigo-700">ヴィラーマ</strong>は、子音字が本来もっている母音
                  <span lang="sa-Latn">a</span>（内在母音）を打ち消す記号です。
                  <span lang="sa-Deva" class="font-devanagari">क</span> は
                  <span lang="sa-Latn">k</span> ではなく
                  <span lang="sa-Latn">ka</span> で、
                  <span lang="sa-Deva" class="font-devanagari">क्</span> ではじめて
                  <span lang="sa-Latn">k</span> になります。子音と一緒に選ぶと、この対比が
                  選択肢に並びます。サンスクリットで語末に立てる子音は限られているため、その9種だけを出題します。
                </p>

                <p>
                  <strong class="text-indigo-700">アヌスヴァーラ・ヴィサルガ</strong>は、単独では
                  発音されず必ず音節に添えて使う記号（<span lang="sa-Latn">ayogavāha</span>）です。
                  そのため記号だけを出題せず、
                  <span lang="sa-Deva" class="font-devanagari">कं</span>（<span lang="sa-Latn">kaṃ</span>）
                  <span lang="sa-Deva" class="font-devanagari">कः</span>（<span lang="sa-Latn">kaḥ</span>）
                  のように子音に添えた形で出題します。
                </p>

                <p>
                  <strong class="text-indigo-700">結合子音</strong>は、ヴィラーマの後ろに子音が
                  続いたときに 2 つが 1 つの形に組まれたものです。
                  <span lang="sa-Deva" class="font-devanagari">क्</span>（<span lang="sa-Latn">k</span>）
                  に <span lang="sa-Deva" class="font-devanagari">त</span>（<span lang="sa-Latn">ta</span>）
                  が続くと <span lang="sa-Deva" class="font-devanagari">क्त</span>（<span lang="sa-Latn">kta</span>）
                  になります。
                </p>
                <ul class="list-disc pl-5 space-y-1 text-sm text-gray-600">
                  <li>
                    実際の文章には数百種類の組み合わせが現れるので、網羅はしていません。よく出会う
                    ${counts.conjuncts}種を通して、組まれ方に慣れるための出題です。
                  </li>
                  <li>
                    <span lang="sa-Deva" class="font-devanagari">र</span> は位置で形が変わります。
                    後ろに来ると下に付き（<span lang="sa-Deva" class="font-devanagari">प्र</span> =
                    <span lang="sa-Latn">pra</span>）、前に来ると母音を失って上に鉤として乗ります
                    （<span lang="sa-Deva" class="font-devanagari">र्व</span> =
                    <span lang="sa-Latn">rva</span>。<span lang="sa-Latn">rava</span> ではありません）。
                  </li>
                  <li>
                    <span lang="sa-Deva" class="font-devanagari">क्ष</span>（<span lang="sa-Latn">kṣa</span>）
                    と <span lang="sa-Deva" class="font-devanagari">ज्ञ</span>（<span lang="sa-Latn">jña</span>）
                    だけは、もとの字の形が残らないので分解して読めません。この形ごと覚えてください。
                  </li>
                  <li>
                    ここでは母音記号を付けない形だけを出題します。実際の文章では
                    <span lang="sa-Deva" class="font-devanagari">स्थि</span>
                    <span lang="sa-Deva" class="font-devanagari">क्ते</span>
                    のように母音記号が付いて現れます。
                  </li>
                </ul>
              </div>
            </details>
          </div>`
        : nothing}
    </div>
  `;
}
